import { SearchQuery } from "shared/data/reducers/types/storage";
import { getFlightCombinationsTree } from "shared/gol-api/flightSearch";
import { isTrue } from "shared/lib/functions";
import FlightCombination from "shared/types/flightCombination";
import FlightOffer from "shared/types/flightOffer";
import FlightStream from "shared/types/flightStream";

const getMarketingAirlines = (flightOption: FlightStream): string[] =>
  flightOption.FlightSegments.FlightSegment.map(
    (flightSegment) => flightSegment.MarketingAirline
  );

const isTransportCompanyAllowed = (
  transportCompany: string,
  allowedTransportCompanies: string[] | null
): boolean =>
  !allowedTransportCompanies ||
  allowedTransportCompanies.includes(transportCompany);

const isFlightOptionMatchingTransportCompanies = (
  flightOption: FlightStream,
  allowedTransportCompanies: string[] | null
): boolean => {
  const marketingAirlines = getMarketingAirlines(flightOption);

  return marketingAirlines.some((marketingAirline) =>
    isTransportCompanyAllowed(marketingAirline, allowedTransportCompanies)
  );
};

const getFlightOptionsByKey = (
  flightOffer: FlightOffer
): Map<string, FlightStream> =>
  new Map(
    flightOffer.FlightItinerary.FlightStream.flatMap((flightStream) =>
      flightStream.map((flightOption) => [flightOption.Key, flightOption])
    )
  );

const getFlightCombinationOptions = (
  flightCombination: FlightCombination,
  flightOptionsByKey: Map<string, FlightStream>
): FlightStream[] | null => {
  const flightOptions = flightCombination.FlightOptionCombinations.FlightOptionCombination.map(
    ({ Key }) => flightOptionsByKey.get(Key)
  );

  return flightOptions.every(Boolean)
    ? (flightOptions as FlightStream[])
    : null;
};

const isFlightCombinationMatchingTransportCompanies = (
  flightCombination: FlightCombination,
  flightOptionsByKey: Map<string, FlightStream>,
  allowedTransportCompanies: string[] | null
): boolean => {
  const flightOptions = getFlightCombinationOptions(
    flightCombination,
    flightOptionsByKey
  );

  return Boolean(
    flightOptions?.length &&
      flightOptions.every((flightOption) =>
        isFlightOptionMatchingTransportCompanies(
          flightOption,
          allowedTransportCompanies
        )
      )
  );
};

const isStrictFlightCombinationMatchingTransportCompanies = (
  flightCombination: FlightCombination,
  flightOptionsByKey: Map<string, FlightStream>,
  allowedTransportCompanies: string[] | null,
  withoutCombinations: boolean
): boolean => {
  const flightOptions = getFlightCombinationOptions(
    flightCombination,
    flightOptionsByKey
  );
  const flightSegments = flightOptions?.flatMap(
    (flightOption) => flightOption.FlightSegments.FlightSegment
  );

  if (!flightSegments?.length) {
    return false;
  }

  const firstMarketingAirline = flightSegments[0].MarketingAirline;

  return flightSegments.every(
    (flightSegment) =>
      isTransportCompanyAllowed(
        flightSegment.MarketingAirline,
        allowedTransportCompanies
      ) &&
      (!withoutCombinations ||
        (flightSegment.MarketingAirline === firstMarketingAirline &&
          (!flightSegment.OperatingAirline ||
            flightSegment.OperatingAirline === flightSegment.MarketingAirline)))
  );
};

const applyAvailableFlightCombinations = (
  flightOffer: FlightOffer,
  availableFlightCombinations: FlightCombination[]
): boolean => {
  if (availableFlightCombinations.length === 0) {
    return false;
  }

  const availableFlightOptionKeys = new Set(
    availableFlightCombinations.flatMap((flightCombination) =>
      flightCombination.FlightOptionCombinations.FlightOptionCombination.map(
        ({ Key }) => Key
      )
    )
  );

  // eslint-disable-next-line no-param-reassign
  flightOffer.FlightItinerary.FlightStream = flightOffer.FlightItinerary.FlightStream.map(
    (flightStream) =>
      flightStream.filter(({ Key }) => availableFlightOptionKeys.has(Key))
  );
  // eslint-disable-next-line no-param-reassign
  flightOffer.FlightCombinations = availableFlightCombinations;
  // eslint-disable-next-line no-param-reassign
  flightOffer.CurrentCombinations = getFlightCombinationsTree(
    availableFlightCombinations
  );

  return true;
};

const filterFlightCombinations = (
  flightOffer: FlightOffer,
  isFlightCombinationAllowed: (
    flightCombination: FlightCombination,
    flightOptionsByKey: Map<string, FlightStream>
  ) => boolean
): boolean => {
  const flightOptionsByKey = getFlightOptionsByKey(flightOffer);
  const availableFlightCombinations = flightOffer.FlightCombinations.filter(
    (flightCombination) =>
      isFlightCombinationAllowed(flightCombination, flightOptionsByKey)
  );

  return applyAvailableFlightCombinations(
    flightOffer,
    availableFlightCombinations
  );
};

const filterStrictTransportCompanyCombinations = (
  searchQuery: SearchQuery,
  flightOffer: FlightOffer,
  allowedTransportCompanies: string[] | null
): boolean =>
  filterFlightCombinations(
    flightOffer,
    (flightCombination, flightOptionsByKey) =>
      isStrictFlightCombinationMatchingTransportCompanies(
        flightCombination,
        flightOptionsByKey,
        allowedTransportCompanies,
        isTrue(searchQuery.onlySelectedNoCombinations)
      )
  );

const filterTransportCompanyCombinations = (
  flightOffer: FlightOffer,
  allowedTransportCompanies: string[]
): boolean =>
  filterFlightCombinations(
    flightOffer,
    (flightCombination, flightOptionsByKey) =>
      isFlightCombinationMatchingTransportCompanies(
        flightCombination,
        flightOptionsByKey,
        allowedTransportCompanies
      )
  );

const filterTransportCompanies = (searchQuery: SearchQuery) => (
  flightOffer: FlightOffer
): boolean => {
  const allowedTransportCompanies = searchQuery.transport_companies
    ? searchQuery.transport_companies.split("-")
    : null;

  if (
    isTrue(searchQuery.onlySelected) ||
    isTrue(searchQuery.onlySelectedNoCombinations)
  ) {
    return filterStrictTransportCompanyCombinations(
      searchQuery,
      flightOffer,
      allowedTransportCompanies
    );
  }

  if (allowedTransportCompanies) {
    return filterTransportCompanyCombinations(
      flightOffer,
      allowedTransportCompanies
    );
  }

  return true;
};

export default filterTransportCompanies;
